import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/api";

export default function HumanReview() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState(() => new URLSearchParams(window.location.search).get('story'));
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState("");
  const queryClient = useQueryClient();
  const decide = async (action) => {
    if (!selected || !note.trim()) { setActionError("Enter a review note first."); return; }
    const headline = detail.data?.workflow_summary?.headline || "this story";
    const message = action === "approve"
      ? `Approve ${headline}? This may enter the automatic publishing queue if the backend checks pass.`
      : `Reject ${headline}? This records a non-publishable decision.`;
    if (!window.confirm(message)) return;
    setPending(true); setActionError("");
    try {
      await adminApi.decideReview(selected, action, note.trim());
      setSelected(null); setNote("");
      await queryClient.invalidateQueries({ queryKey: ["newsroom-review-queue"] });
    } catch (error) {
      const detailText = error.response?.data?.detail;
      setActionError(typeof detailText === "string" ? detailText : "Decision failed; story was not changed.");
    } finally { setPending(false); }
  };
  const { data = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["newsroom-review-queue", page, category],
    queryFn: () => adminApi.queue({ view: 'legacy-review', category, page, page_size: 50 }),
    enabled: user?.role === 'admin',
  });
  const detail = useQuery({
    queryKey: ["newsroom-review-detail", selected],
    queryFn: () => adminApi.reviewDetail(selected),
    enabled: Boolean(selected) && user?.role === 'admin',
  });

  if (user?.role !== 'admin') return <p role="alert">Administrator access required.</p>;
  const items = data.items || [];
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="td-eyebrow" style={{ color: "#FF8A1F" }}>NEWSROOM WORKFLOW</p>
          <h1 className="text-3xl font-display font-extrabold text-white">Legacy Human Review</h1>
          <p className="text-[#A7ABB0] text-sm mt-2">Historical HUMAN_REVIEW records. Approval requires every mandatory publication gate and a complete bilingual bundle. No records are automatically migrated.</p>
        </div>
        <div className="flex gap-4 text-sm">
          <Link to="/admin/queue" className="text-[#A7ABB0] hover:text-white">← Editorial Queue</Link>
          <button onClick={() => refetch()} className="text-[#FF8A1F]">Refresh</button>
        </div>
      </header>
      {selected && (
        <section className="rounded-lg border border-white/10 bg-[#111315] p-5 space-y-3" aria-label="Review details" id="selected-review-details">
          <div className="flex justify-between gap-4">
            <h2 className="font-display font-bold text-white">Review details</h2>
            <button className="text-[#A7ABB0] text-sm" onClick={() => setSelected(null)}>Close</button>
          </div>
          {detail.isLoading && <p className="text-[#A7ABB0]">Loading details…</p>}
          {detail.isError && <p role="alert" className="text-red-400">Could not load details.</p>}
          {detail.data && (
            <>
              <p className="text-white text-sm">{detail.data.workflow_summary?.headline}</p>
              <p className="text-[#A7ABB0] text-sm">Reason: {[...(detail.data.workflow_summary?.review_reasons || []), ...(detail.data.workflow_summary?.blocking_factors || [])].filter((v, i, a) => a.indexOf(v) === i).join(", ") || "Manual assessment required"}</p>
              {detail.data.publish_gate?.details?.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-white text-sm font-semibold">Flagged article statements</h3>
                  {detail.data.publish_gate.details.slice(0, 12).map((entry, index) => <p key={index} className="text-[#A7ABB0] text-xs">{entry.field}: {entry.text}</p>)}
                </div>
              )}
              <p className="text-[#A7ABB0] text-xs">Research: {detail.data.research_packet_summary?.research_status || "Not available"} · QC: {detail.data.workflow_summary?.qc_decision || "Not available"}</p>
              <div className="space-y-3 text-sm text-[#A7ABB0]">
                <h3 className="font-bold text-white">Publication readiness</h3><p>{detail.data.approval_readiness?.can_approve ? 'Mandatory local publication checks passed' : 'Approval blocked'}</p><ul>{detail.data.approval_readiness?.blockers?.map((item, i) => <li key={i}>{item}</li>)}</ul>
                <h3 className="font-bold text-white">Source evidence</h3>{detail.data.source_evidence?.map((item, i) => <details key={i}><summary>{item.publisher || item.title || 'Source'} · {item.scope || 'Research evidence'}</summary><p className="whitespace-pre-wrap">{item.text || item.short_excerpt || item.evidence_note || 'See research claim statuses below.'}</p>{/^https?:\/\//i.test(item.url || '') && <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[#FF8A1F] break-all">{item.url}</a>}</details>)}
                <details><summary>Factual safeguards and research gaps</summary><pre className="whitespace-pre-wrap break-words text-xs">{JSON.stringify({ claims: detail.data.claim_statuses, research: detail.data.research_packet_summary, qc: detail.data.qc_review, gate: detail.data.publish_gate }, null, 2)}</pre></details>
                <details><summary>Draft</summary><h4>{detail.data.writer_draft?.headline}</h4><p className="whitespace-pre-wrap">{detail.data.writer_draft?.body_markdown || 'Draft missing'}</p></details>
                <details><summary>Historical audit</summary><pre className="whitespace-pre-wrap break-words text-xs">{JSON.stringify({ previous: detail.data.human_review, history: detail.data.human_review_history, revisions: detail.data.revision_history }, null, 2)}</pre></details>
              </div>
              <div className="border-t border-white/10 pt-4 space-y-3">
                <label htmlFor="review-note" className="block text-white text-sm">Editor review note (required)</label>
                <textarea id="review-note" value={note} maxLength={500} rows={3}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full rounded border border-white/20 bg-[#181a1c] p-3 text-white text-sm"
                  placeholder="Explain source verification or why the story should be rejected." />
                <p className="text-[#A7ABB0] text-xs">Approval is checked again by the backend; unresolved factual or source holds cannot be bypassed. An approved story may be automatically published.</p>
                {actionError && <p role="alert" className="text-red-400 text-sm">{actionError}</p>}
                <div className="flex flex-wrap gap-3">
                  <button type="button" disabled={pending || !note.trim() || !detail.data.approval_readiness?.can_approve} onClick={() => decide("approve")}
                    className="rounded bg-[#3ecf8e] px-4 py-2 text-black font-semibold text-sm disabled:opacity-40">
                    {pending ? "Saving…" : "Approve"}
                  </button>
                  <button type="button" disabled={pending || !note.trim()} onClick={() => decide("reject")}
                    className="rounded border border-red-400 px-4 py-2 text-red-300 font-semibold text-sm disabled:opacity-40">
                    Reject
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      )}
      <label className="text-sm text-[#A7ABB0]">Category <select aria-label="Review category" className="bg-[#111315] p-2" value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>{['all','ai','robotics','cybersecurity','future-tech','business','guides'].map(item => <option key={item}>{item}</option>)}</select></label>
      <section className="rounded-lg border border-white/10 bg-[#111315] overflow-hidden">
        <p className="p-4 text-[#A7ABB0] text-sm">Human Review · {data.total ?? 0} stories shown (50 per page)</p>
        {isLoading && <p className="p-4 text-[#A7ABB0]">Loading…</p>}
        {isError && <p role="alert" className="p-4 text-red-400">Could not load review queue. Please retry.</p>}
        {!isLoading && !isError && items.length === 0 && <p className="p-4 text-[#A7ABB0]">No stories currently waiting for review.</p>}
        {items.map((story) => (
          <button key={story.story_id} type="button" onClick={() => { setSelected(story.story_id); setNote(""); setActionError(""); }} aria-expanded={selected === story.story_id} aria-controls="selected-review-details"
            className={"block w-full text-left border-t border-white/10 p-4 hover:bg-[#1B1E21] " + (selected === story.story_id ? "bg-[#1B1E21]" : "")}>
            <div className="text-white font-medium text-sm">{story.headline || story.story_id}</div>
            <div className="text-[#A7ABB0] text-xs mt-2">{story.category || "Uncategorized"} · HUMAN_REVIEW · {story.updated_at || ""}</div>
            <div className="text-[#FF8A1F] text-xs mt-2">{[...(story.review_reasons || []), ...(story.blocking_factors || [])].filter((v, i, a) => a.indexOf(v) === i).join(", ") || "Review required — open for details"}</div>
          </button>
        ))}
      </section>
      <nav className="flex justify-between text-sm text-[#A7ABB0]" aria-label="Review pagination"><button disabled={page === 1} onClick={() => setPage(n => n-1)}>Previous</button><span>Page {page}</span><button disabled={page*50 >= (data.total || 0)} onClick={() => setPage(n => n+1)}>Next</button></nav>
    </div>
  );
}
